/**
 * Notificações (redesign admin, Fase 4): central de leitura das notificações
 * operacionais do admin-runtime (`/api/admin/notifications`), a mesma fonte do
 * dashboard e da Sincronização. Somente leitura: não há contrato para marcar
 * como lida, então a tela não oferece o botão.
 */
import { createElement as h, useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Bell, Info, OctagonAlert } from "lucide-react";
import { DatasetBoundary } from "../components/DatasetBoundary.js";
import type { DashboardDataset } from "../dashboard/kpis.js";
import {
  SEVERITY_LABELS,
  filterNotifications,
  notificationCounts,
  type NotificationReadFilter,
  type NotificationSeverityFilter
} from "../dashboard/notification-view.js";
import { formatCount, formatDateTime } from "../format.js";
import { Card, EmptyState, PageHeader, Segmented, StatTile } from "../ui/index.js";

const ICONS = { critical: OctagonAlert, warning: AlertTriangle, info: Info } as const;

export function NotificacoesView({ dataset }: { dataset: DashboardDataset }): ReactElement {
  const [severity, setSeverity] = useState<NotificationSeverityFilter>("todas");
  const [read, setRead] = useState<NotificationReadFilter>("todas");
  const counts = notificationCounts(dataset.notifications);
  const rows = filterNotifications(dataset.notifications, severity, read);
  return h(
    "div",
    { className: "page page--notificacoes flex flex-col gap-6", "data-page": "notificacoes" },
    h(PageHeader, { title: "Notificações", description: "Alertas operacionais do sistema e das integrações." }),
    h(
      "section",
      { "aria-label": "Resumo das notificações", className: "grid grid-cols-1 gap-4 sm:grid-cols-3" },
      h(StatTile, { label: "Total", value: formatCount(counts.total), icon: Bell, "data-kpi": "notif-total" }),
      h(StatTile, { label: "Não lidas", value: formatCount(counts.unread), icon: Bell, "data-kpi": "notif-nao-lidas" }),
      h(StatTile, { label: "Críticas não lidas", value: formatCount(counts.critical), icon: OctagonAlert, "data-kpi": "notif-criticas" })
    ),
    h(
      "div",
      { className: "flex flex-wrap gap-3" },
      h(Segmented<NotificationSeverityFilter>, {
        label: "Severidade",
        value: severity,
        onChange: setSeverity,
        options: [
          { value: "todas", label: "Todas" },
          { value: "critical", label: SEVERITY_LABELS.critical },
          { value: "warning", label: SEVERITY_LABELS.warning },
          { value: "info", label: SEVERITY_LABELS.info }
        ]
      }),
      h(Segmented<NotificationReadFilter>, {
        label: "Leitura",
        value: read,
        onChange: setRead,
        options: [
          { value: "todas", label: "Todas" },
          { value: "nao-lidas", label: "Não lidas" }
        ]
      })
    ),
    rows.length === 0
      ? h(EmptyState, { title: "Nenhuma notificação neste recorte" })
      : h(
          Card,
          null,
          h(
            "ul",
            { className: "m-0 flex list-none flex-col divide-y divide-border p-0", "aria-label": "Notificações" },
            rows.map((n) => {
              const Icon = ICONS[n.severity];
              return h(
                "li",
                {
                  key: n.id,
                  "data-notification": n.id,
                  "data-severity": n.severity,
                  "data-read": String(n.read),
                  className: "flex items-start gap-3 py-3 text-sm"
                },
                h(Icon, { className: "mt-0.5 h-5 w-5 shrink-0", "aria-hidden": true, focusable: false }),
                h(
                  "div",
                  { className: "min-w-0 flex-1" },
                  h(
                    "p",
                    { className: n.read ? "m-0 font-semibold text-body" : "m-0 font-extrabold text-ink" },
                    n.title,
                    " ",
                    h("span", { className: "text-xs font-bold text-muted" }, `· ${SEVERITY_LABELS[n.severity]}${n.read ? "" : " · não lida"}`)
                  ),
                  n.body ? h("p", { className: "m-0 mt-1 text-muted" }, n.body) : null,
                  n.related_entity_type === "order" && n.related_entity_id
                    ? h(Link, { to: `/pedidos/${n.related_entity_id}`, className: "text-xs font-semibold text-brand-text" }, "Abrir pedido")
                    : null
                ),
                h("span", { className: "shrink-0 text-xs text-muted" }, formatDateTime(n.created_at))
              );
            })
          )
        )
  );
}

export function Notificacoes(): ReactElement {
  return h(DatasetBoundary, { children: (dataset: DashboardDataset) => h(NotificacoesView, { dataset }) });
}
